"use client";

import React, { useMemo, useState, useCallback } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { InvoiceDetailsModal } from "./InvoiceDetailsModal";
import { MatchQBCustomerDrawer } from "../match/MatchQBCustomerDrawer";
import { useInvoiceData } from "./hooks/useInvoiceData";
import { useInvoiceMappings } from "./hooks/useInvoiceMappings";
import {
  getDisplayNameForCustomer,
  getAllCustomerDisplayNames,
  getInvoiceCustomerName,
} from "./utils/invoiceDisplayHelpers";
import { InvoiceList } from "./components/InvoiceList";
import type { InvoiceDisplayItem } from "@/lib/utils/crm/quickbooks-company-ref";

interface QuickBooksInvoiceViewerProps {
  customerName: string;
  contactId?: string; // preferred path: use contact's QB ref
}

export function QuickBooksInvoiceViewer({ customerName, contactId }: QuickBooksInvoiceViewerProps) {
  console.log("🔍 [QuickBooksInvoiceViewer] Component rendered with customerName:", customerName);

  // Get invoice data using custom hook
  const {
    contact,
    company,
    qbCustomerRefs,
    qbCustomerIds,
    invoices,
    isLoadingInvoices,
    invoiceError,
  } = useInvoiceData(customerName, contactId);

  // Get invoice mappings using custom hook
  const {
    existingMappings,
    handleConfirmQBCustomerMapping,
    handleRemoveQBCustomerMapping,
    isRemoving,
  } = useInvoiceMappings(company);

  // Helper function to get the display name for a specific customer connection
  const getDisplayNameForCustomerFn = useCallback(
    (customerId: string) => {
      return getDisplayNameForCustomer(customerId, company, customerName);
    },
    [company, customerName]
  );

  // Get display names for messaging
  const customerDisplayNames = useMemo(
    () => getAllCustomerDisplayNames(company, qbCustomerRefs, customerName, contact),
    [company, qbCustomerRefs, customerName, contact]
  );
  const displayNamesText = useMemo(() => {
    const primaryDisplayName = customerDisplayNames[0];
    return customerDisplayNames.length > 1
      ? `${customerDisplayNames.slice(0, -1).join(", ")} and ${customerDisplayNames[customerDisplayNames.length - 1]}`
      : primaryDisplayName;
  }, [customerDisplayNames]);

  // Simplified function to get display customer name for an invoice using the centralized utility
  const getInvoiceCustomerNameFn = useCallback(
    (invoiceItem: InvoiceDisplayItem) => {
      return getInvoiceCustomerName(invoiceItem, company);
    },
    [company]
  );

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedRealmId, setSelectedRealmId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMatchQBCustomerDrawerOpen, setIsMatchQBCustomerDrawerOpen] = useState(false);

  const handleViewDetails = (invoiceId: string, realmId?: string) => {
    setSelectedInvoiceId(invoiceId);
    setSelectedRealmId(realmId || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoiceId(null);
    setSelectedRealmId(null);
  };

  const handleOpenMatchQBCustomerDrawer = () => {
    setIsMatchQBCustomerDrawerOpen(true);
  };

  const handleCloseMatchQBCustomerDrawer = (open: boolean) => {
    setIsMatchQBCustomerDrawerOpen(open);
    if (!open) {
      // Drawer is closing
    }
  };

  const handleConfirmMapping = async (
    qbCustomerId: string,
    qbCustomerName?: string,
    qbRealmId?: string
  ) => {
    await handleConfirmQBCustomerMapping(qbCustomerId, qbCustomerName, qbRealmId);
    setIsMatchQBCustomerDrawerOpen(false);
  };

  if (!customerName && qbCustomerIds.length === 0) {
    return (
      <Alert className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Please select a customer to view QuickBooks invoices.</AlertDescription>
      </Alert>
    );
  }

  // Show loading state while loading invoices
  if (isLoadingInvoices) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <LoadingSpinner size="lg" />
        <p className="mt-2 text-sm text-gray-600">
          Loading invoices for &ldquo;{displayNamesText}&rdquo;...
        </p>
      </div>
    );
  }

  // Show error if invoice loading failed
  if (invoiceError) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load invoices for &ldquo;{displayNamesText}&rdquo;: {invoiceError.message}
        </AlertDescription>
      </Alert>
    );
  }

  // Show no invoices found
  if (invoices.length === 0) {
    return (
      <Alert className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No QuickBooks invoices found for &ldquo;{displayNamesText}&rdquo;.
        </AlertDescription>
      </Alert>
    );
  }

  // Show invoices
  return (
    <>
      <InvoiceList
        invoices={invoices}
        displayNamesText={displayNamesText}
        onViewDetails={handleViewDetails}
        getInvoiceCustomerName={getInvoiceCustomerNameFn}
        contactId={contactId}
        onOpenMatchDrawer={handleOpenMatchQBCustomerDrawer}
        existingMappings={existingMappings}
        onRemoveMapping={handleRemoveQBCustomerMapping}
        getDisplayNameForCustomer={getDisplayNameForCustomerFn}
        isRemoving={isRemoving}
      />

      <InvoiceDetailsModal
        key={`${selectedInvoiceId}-${selectedRealmId}`}
        invoiceId={selectedInvoiceId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        realmId={selectedRealmId || undefined}
      />

      {/* Match QB Customer Drawer */}
      {contactId && (
        <MatchQBCustomerDrawer
          open={isMatchQBCustomerDrawerOpen}
          onOpenChange={handleCloseMatchQBCustomerDrawer}
          title="Add QuickBooks Customer Mapping"
          description={`Add a QuickBooks customer mapping for ${company?.name || contact?.name || contact?.company_name}`}
          contactId={contactId}
          companyId={company?.id}
          onConfirm={handleConfirmMapping}
        />
      )}
    </>
  );
}
